// src/components/Layout/Header.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import UpgradeModal from '../UpgradeModal';
import './Header.css';

const MoonIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 12.79A9 9 0 0111.21 3a7 7 0 000 14A9 9 0 0021 12.79z" />
  </svg>
);

const SunIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2" />
    <path d="M12 20v2" />
    <path d="M4.93 4.93l1.41 1.41" />
    <path d="M17.66 17.66l1.41 1.41" />
    <path d="M2 12h2" />
    <path d="M20 12h2" />
    <path d="M6.34 17.66l-1.41 1.41" />
    <path d="M19.07 4.93l-1.41 1.41" />
  </svg>
);

const PUBLIC_IMAGE_ROOT = process.env.PUBLIC_URL || '';
const WITH_TAGS_IMAGE = `${PUBLIC_IMAGE_ROOT}/images2/with_tags.jpg`;
const WITHOUT_TAGS_IMAGE = `${PUBLIC_IMAGE_ROOT}/images2/without_tags.jpg`;
const COLORED_TAGS_IMAGE = `${PUBLIC_IMAGE_ROOT}/images2/tags_colored.jpg`;
const NEUTRAL_TAGS_IMAGE = `${PUBLIC_IMAGE_ROOT}/images2/tags_neutral.jpg`;
const LOGO_LIGHT = `${PUBLIC_IMAGE_ROOT}/images2/Logos AWD/Horizontal/Colored_no_background.png`;
const LOGO_DARK = `${PUBLIC_IMAGE_ROOT}/images2/Logos AWD/Horizontal/Colored_background.png`;

function Header({ user, sidebarState, onToggleSidebar, tagPreference = 'with-tags', onTagPreferenceChange, tagStylePreference = 'colored', onTagStylePreferenceChange, customizeOpen = false, onCustomizeOpenChange, resultsText = '', activeFilters = [], onRemoveFilter }) {
  const { darkMode, setDarkMode } = useTheme();
  const navigate = useNavigate();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    if (!customizeOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (typeof onCustomizeOpenChange === 'function') {
          onCustomizeOpenChange(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [customizeOpen, onCustomizeOpenChange]);

  const isWithTagsSelected = tagPreference !== 'without-tags';

  const handleToggleTags = () => {
    if (typeof onTagPreferenceChange === 'function') {
      onTagPreferenceChange(isWithTagsSelected ? 'without-tags' : 'with-tags');
    }
  };

  const isColoredTagsSelected = tagStylePreference !== 'neutral';

  const handleToggleTagStyle = () => {
    if (typeof onTagStylePreferenceChange === 'function') {
      onTagStylePreferenceChange(isColoredTagsSelected ? 'neutral' : 'colored');
    }
  };

  const closeCustomizePanel = () => {
    if (typeof onCustomizeOpenChange === 'function') {
      onCustomizeOpenChange(false);
    }
  };

  const today = new Date().toLocaleDateString('fr-FR', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });

  const goToHome = () => {
    // Redirection vers la landing page sans déconnexion
    window.location.href = "https://www.automotive-database.com/";
  };

  const handleUpgrade = () => {
    setShowUpgradeModal(true);
  };

  // Check if user should see trial badge
  const showTrialBadge = user && user.plan !== 'pro' && user.subscriptionStatus === 'on_trial';

  return (
    <>
      <header className="app-header">
        <div className="header-left">
          {showTrialBadge && (
            <div className="header-trial-badge">
              <span className="trial-badge-text">Enjoy your 7 day free trial</span>
              <button onClick={handleUpgrade} className="trial-badge-upgrade-btn">
                Upgrade
              </button>
            </div>
          )}

          {resultsText && (
            <div className="header-results-text">
              {resultsText}
            </div>
          )}

          {activeFilters && activeFilters.length > 0 && (
            <div className="header-active-filters">
              {activeFilters.map((filter) => (
                <span key={filter.key} className="header-filter-chip">
                  <span className="header-filter-chip-label">{filter.label}</span>
                  <span className="header-filter-chip-value">{filter.value}</span>
                  <button
                    type="button"
                    className="header-filter-chip-remove"
                    onClick={() => onRemoveFilter && onRemoveFilter(filter.filterKey, filter.value, filter)}
                    aria-label={`Remove ${filter.label} filter ${filter.value}`}
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="header-center">
          {/* Empty center section */}
        </div>

        <div className="header-right">
          {/* Dark mode and Customize buttons moved to Sidebar */}
        </div>
      </header>

      <div
        className={`customize-panel-overlay ${customizeOpen ? 'is-visible' : ''}`}
        role="presentation"
        aria-hidden={!customizeOpen}
        onClick={closeCustomizePanel}
      />

      <aside
        id="customize-panel"
        className={`customize-panel ${customizeOpen ? 'is-open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="customize-panel-title"
        aria-hidden={!customizeOpen}
      >
        <div className="customize-panel-header">
          <div className="customize-panel-title">
            <h2 id="customize-panel-title">Processing Settings</h2>
            <span className="customize-panel-gear" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <path d="M12 15.5A3.5 3.5 0 1 0 12 8.5a3.5 3.5 0 0 0 0 7z" />
                <path d="M19.4 15a1 1 0 0 0 .2 1.1l.1.1a2 2 0 0 1 0 2.8l-.8.8a2 2 0 0 1-2.8 0h-.1a1 1 0 0 0-1.1-.2 1 1 0 0 0-.6.9v.3a2 2 0 0 1-2 2h-1.1a2 2 0 0 1-2-2v-.3a1 1 0 0 0-.6-.9 1 1 0 0 0-1.1.2h-.1a2 2 0 0 1-2.8 0l-.8-.8a2 2 0 0 1 0-2.8l.1-.1a1 1 0 0 0 .2-1.1 1 1 0 0 0-.9-.6H3.5a2 2 0 0 1-2-2v-1.1a2 2 0 0 1 2-2h.3a1 1 0 0 0 .9-.6 1 1 0 0 0-.2-1.1l-.1-.1a2 2 0 0 1 0-2.8l.8-.8a2 2 0 0 1 2.8 0 .9.9 0 0 0 1.1.2h.1a1 1 0 0 0 .6-.9V3.5a2 2 0 0 1 2-2h1.1a2 2 0 0 1 2 2v.3a1 1 0 0 0 .6.9 1 1 0 0 0 1.1-.2h.1a2 2 0 0 1 2.8 0l.8.8a2 2 0 0 1 0 2.8l-.1.1a1 1 0 0 0-.2 1.1 1 1 0 0 0 .9.6h.3a2 2 0 0 1 2 2v1.1a2 2 0 0 1-2 2h-.3a1 1 0 0 0-.9.6z" />
              </svg>
            </span>
          </div>
          <button
            type="button"
            className="customize-panel-close"
            onClick={closeCustomizePanel}
            aria-label="Close customization panel"
          >
            &times;
          </button>
        </div>
        <div className="customize-panel-separator" />
        <div className="customize-panel-body">
          <section className="customize-section customize-section--visibility">
            <div className="customize-illustration">
              <span className="customize-illustration-title">With tags</span>
              <img src={WITH_TAGS_IMAGE} alt="Aperçu des cartes avec tags" loading="lazy" />
            </div>
            <div className="customize-illustration">
              <span className="customize-illustration-title">Without tags</span>
              <img src={WITHOUT_TAGS_IMAGE} alt="Aperçu des cartes sans tags" loading="lazy" />
            </div>
            <div className="customize-toggle-row">
              <span className={`customize-toggle-label ${isWithTagsSelected ? 'is-active' : ''}`}>With Tags</span>
              <label className="customize-switch">
                <input
                  type="checkbox"
                  checked={isWithTagsSelected}
                  onChange={handleToggleTags}
                />
                <span className="customize-switch-track">
                  <span className="customize-switch-thumb" />
                </span>
              </label>
              <span className={`customize-toggle-label ${!isWithTagsSelected ? 'is-active' : ''}`}>Without Tags</span>
            </div>
          </section>

          <section className="customize-section customize-section--style">
            <div className="customize-illustration">
              <span className="customize-illustration-title">Colored</span>
              <img src={COLORED_TAGS_IMAGE} alt="Aperçu des tags colorés" loading="lazy" />
            </div>
            <div className="customize-illustration">
              <span className="customize-illustration-title">Neutral</span>
              <img src={NEUTRAL_TAGS_IMAGE} alt="Aperçu des tags neutres" loading="lazy" />
            </div>
            <div className="customize-toggle-row">
              <span className={`customize-toggle-label ${isColoredTagsSelected ? 'is-active' : ''}`}>Colored</span>
              <label className="customize-switch">
                <input
                  type="checkbox"
                  checked={isColoredTagsSelected}
                  onChange={handleToggleTagStyle}
                />
                <span className="customize-switch-track">
                  <span className="customize-switch-thumb" />
                </span>
              </label>
              <span className={`customize-toggle-label ${!isColoredTagsSelected ? 'is-active' : ''}`}>Neutral</span>
            </div>
          </section>
        </div>
      </aside>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </>
  );
}

export default Header;
